export const SHIPMENT_PRE_RECEIPT_TIMELINE = [
  { id: "REQUEST_SUBMITTED", label: "Request submitted" },
  { id: "LABEL_ISSUED", label: "Label issued" },
  { id: "CLIENT_CONTACTED", label: "Client contacted" },
  { id: "CLIENT_REQUEST_CONFIRMED", label: "Client request confirmed" },
] as const;

// System-generated milestone set by the warehouse spreadsheet import when a
// shipment's cargo has arrived at the origin warehouse and been measured. Not
// staff-triggerable (kept out of the pre-receipt/package sets below).
export const RECEIVED_AT_WAREHOUSE_EVENT = "RECEIVED_AT_WAREHOUSE";

export const WAREHOUSE_TIMELINE = [
  { id: RECEIVED_AT_WAREHOUSE_EVENT, label: "Received & measured at warehouse" },
] as const;

// Shipment-level milestones shown as one section (pre-receipt + warehouse
// receipt); per-package steps live in PACKAGE_TIMELINE.
export const SHIPMENT_TIMELINE = [
  ...SHIPMENT_PRE_RECEIPT_TIMELINE,
  ...WAREHOUSE_TIMELINE,
];

export const PACKAGE_TIMELINE = [
  { id: "SHIPPED_TO_GHANA", label: "Shipped to Ghana" },
  { id: "ARRIVED_IN_GHANA", label: "Arrived in Ghana" },
  { id: "CLEARED_AND_INVENTORIED", label: "Cleared & inventoried" },
  { id: "READY_FOR_DELIVERY", label: "Ready for delivery" },
  { id: "OUT_FOR_DELIVERY", label: "Out for delivery" },
  { id: "DELIVERED", label: "Delivered" },
] as const;

export type ShipmentPreReceiptEventId =
  (typeof SHIPMENT_PRE_RECEIPT_TIMELINE)[number]["id"];

export type PackageEventId = (typeof PACKAGE_TIMELINE)[number]["id"];

export type ShipmentEventId = ShipmentPreReceiptEventId | PackageEventId;

const ALL_TIMELINE_ENTRIES = [
  ...SHIPMENT_PRE_RECEIPT_TIMELINE,
  ...WAREHOUSE_TIMELINE,
  ...PACKAGE_TIMELINE,
] as const;

const eventLabelMap = new Map<string, string>(
  ALL_TIMELINE_ENTRIES.map((e) => [e.id, e.label]),
);

export function formatEventLabel(eventId: string | null): string {
  if (eventId == null) {
    return "-";
  }
  return eventLabelMap.get(eventId) ?? eventId;
}

const eventIndexMap = new Map<string, number>(
  ALL_TIMELINE_ENTRIES.map((e, i) => [e.id, i]),
);

export function getEventIndex(eventId: string): number {
  return eventIndexMap.get(eventId) ?? -1;
}

export function pickLatestEventId(eventIds: string[]): string | null {
  let bestIdx = -1;
  let bestEvent: string | null = null;
  for (const e of eventIds) {
    const idx = getEventIndex(e);
    if (bestEvent == null || idx > bestIdx) {
      bestIdx = idx;
      bestEvent = e;
    }
  }
  return bestEvent;
}

const SHIPMENT_PRE_RECEIPT_EVENT_IDS: ReadonlySet<string> = new Set(
  SHIPMENT_PRE_RECEIPT_TIMELINE.map((e) => e.id),
);

const PACKAGE_EVENT_IDS: ReadonlySet<string> = new Set(
  PACKAGE_TIMELINE.map((e) => e.id),
);

export function isShipmentPreReceiptEventId(eventId: string): boolean {
  return SHIPMENT_PRE_RECEIPT_EVENT_IDS.has(eventId);
}

export function isPackageEventId(eventId: string): boolean {
  return PACKAGE_EVENT_IDS.has(eventId);
}

export function pickLatestEvent<T extends { event: string; timestamp: Date }>(
  events: T[],
): T | null {
  if (events.length === 0) {
    return null;
  }
  let best = events[0];
  for (let i = 1; i < events.length; i++) {
    if (isLaterEvent(events[i], best)) {
      best = events[i];
    }
  }
  return best;
}

export function isLaterEvent(
  candidate: { event: string | null; timestamp: Date | null },
  current: { event: string | null; timestamp: Date | null },
): boolean {
  if (candidate.event == null || candidate.timestamp == null) {
    return false;
  }
  if (current.timestamp == null || current.event == null) {
    return true;
  }
  if (candidate.timestamp.getTime() !== current.timestamp.getTime()) {
    return candidate.timestamp.getTime() > current.timestamp.getTime();
  }
  return getEventIndex(candidate.event) > getEventIndex(current.event);
}
